import React, { useEffect, useState } from 'react';
import { Formik } from "formik";
import { ToastContainer, toast } from "react-toastify";
import { Link, useSearchParams } from "react-router-dom";
import { FaWallet, FaArrowLeft, FaCheckCircle, FaPaperPlane, FaCoins } from 'react-icons/fa';
import { addVirtualWallets, virtualWallets, getAdminVirtualWalletBalance, getLength } from "../../../api/login/Login";
import Breadcrumbs from '../../../common/breadcrumb/Breadcrumbs';

function AddUpdateVirtualWall() {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryWallet = searchParams.get('wallet') || 'main_wallet';

    const token = window.localStorage.getItem('userToken');
    const [errMessage, setErrorMessage] = useState(false);
    const [getToken, setGetToken] = useState(false);
    const [inpFlg, setInpFlag] = useState(true);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [loadingBalances, setLoadingBalances] = useState(false);

    // Live balance state for the 3 Admin Virtual Wallets
    const [balances, setBalances] = useState({
        main_wallet: '0.00',
        aeps_wallet: '0.00',
        cashback_wallet: '0.00',
    });

    const [selectedWallet, setSelectedWallet] = useState(queryWallet);

    const fetchLiveBalances = async () => {
        setLoadingBalances(true);
        try {
            const res = await getAdminVirtualWalletBalance();
            if (res?.data && res.data.main_wallet !== undefined) {
                setBalances({
                    main_wallet: res.data.main_wallet || '0.00',
                    aeps_wallet: res.data.aeps_wallet || '0.00',
                    cashback_wallet: res.data.cashback_wallet || '0.00',
                });
                setLoadingBalances(false);
                return;
            }
        } catch (err) {
            console.error("getAdminVirtualWalletBalance error, trying getLength fallback:", err);
        }

        try {
            const res2 = await getLength();
            if (res2?.data) {
                setBalances({
                    main_wallet: res2.data.admin_virtual_main_wallet || '0.00',
                    aeps_wallet: res2.data.admin_virtual_aeps_wallet || '0.00',
                    cashback_wallet: res2.data.admin_virtual_cashback_wallet || '0.00',
                });
            }
        } catch (err2) {
            console.error("getLength fallback error:", err2);
        } finally {
            setLoadingBalances(false);
        }
    };

    useEffect(() => {
        fetchLiveBalances();
    }, []);

    useEffect(() => {
        if (queryWallet) {
            setSelectedWallet(queryWallet);
        }
    }, [queryWallet]);

    const initialValues = {
        wallet_type: selectedWallet,
        amount: "",
        orderId: "",
        operatorId: "",
        message: "",
        otp: "",
    };

    const validate = (values) => {
        let errors = {};
        if (!values.amount || Number(values.amount) <= 0) {
            errors.amount = "Please enter a valid amount greater than 0";
        }
        if (!values.wallet_type) {
            errors.wallet_type = "Target wallet is required";
        }
        if (!values.otp) {
            errors.otp = "OTP is required to load virtual balance";
        }
        return errors;
    };

    const sendOtp = async () => {
        setSendingOtp(true);
        setErrorMessage(false);
        try {
            const otpToken = await virtualWallets(token);
            if (otpToken?.data?.token) {
                setGetToken(otpToken.data.token);
            }
            if (otpToken?.error === false || otpToken?.statusCode === 200 || otpToken?.data?.token) {
                toast.success("OTP sent successfully! (Default: 123456)", {
                    position: "top-right",
                });
                setInpFlag(false);
                setOtpSent(true);
            } else {
                toast.error(otpToken?.message || "Failed to generate OTP");
            }
        } catch (error) {
            console.error("OTP send error:", error);
            setErrorMessage(error.message || "Failed to send OTP");
            toast.error(error.message || "Failed to send OTP");
        } finally {
            setSendingOtp(false);
        }
    };

    const getWalletDisplayName = (type) => {
        switch (type) {
            case 'aeps_wallet':
                return 'AEPS Wallet (AW)';
            case 'cashback_wallet':
                return 'Cashback Wallet (CW)';
            case 'main_wallet':
            default:
                return 'Main Wallet (MB)';
        }
    };

    const submitForm = async (values, { resetForm }) => {
        setErrorMessage(false);
        const autoOrderId = values.orderId || `VIRT-${Date.now()}`;
        const autoMsg = values.message || `Virtual Fund Created for ${getWalletDisplayName(values.wallet_type)}`;

        const payload = {
            ...values,
            orderId: autoOrderId,
            message: autoMsg,
            token: getToken,
        };

        try {
            const res = await addVirtualWallets(payload);
            if (res?.statusCode === 200 || res?.error === false || res?.data?.message === "Success") {
                toast.success(`Successfully loaded ₹${Number(values.amount).toLocaleString('en-IN')} into ${getWalletDisplayName(values.wallet_type)}!`, {
                    position: "top-center",
                });
                resetForm({
                    values: {
                        wallet_type: values.wallet_type,
                        amount: "",
                        orderId: "",
                        operatorId: "",
                        message: "",
                        otp: "",
                    }
                });
                setInpFlag(true);
                setOtpSent(false);
                // Immediately refresh live top cards!
                fetchLiveBalances();
            } else {
                const msg = res?.data?.message || res?.message || "Failed to load virtual balance";
                setErrorMessage(msg);
                toast.error(msg);
            }
        } catch (error) {
            console.error("Submit error:", error);
            setErrorMessage(error.message || "Something went wrong. Please try again.");
            toast.error(error.message || "Something went wrong.");
        }
    };

    return (
        <>
            <Breadcrumbs breadCrumbsTitle={"Virtual Wallet"} />
            <div className="container-fluid py-3 px-4">

                {/* --- 1. TOP LIVE ADMIN VIRTUAL BALANCE CARDS --- */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h4 className="fw-bold mb-1" style={{ color: '#1e293b' }}>
                            <FaCoins className="me-2 text-primary" />
                            Admin Virtual Supply Overview
                        </h4>
                        <p className="text-muted mb-0" style={{ fontSize: '0.88rem' }}>
                            Current live digits available across Admin supply pools to disburse for retailer requests.
                        </p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <Link to="/admin/wallet/virtual-wallet" className="btn btn-primary btn-sm rounded-pill px-3 fw-semibold shadow-sm">
                            <FaWallet className="me-1" /> View Virtual Wallet Report
                        </Link>
                        <Link to="/admin" className="btn btn-outline-secondary btn-sm rounded-pill px-3">
                            <FaArrowLeft className="me-1" /> Back to Dashboard
                        </Link>
                    </div>
                </div>

                <div className="row g-3 mb-4">
                    {/* Main Virtual Wallet Card */}
                    <div className="col-xl-4 col-md-6">
                        <div
                            className="dashboard-card card-gradient-blue p-3 shadow-sm rounded-3 cursor-pointer"
                            style={{ cursor: 'pointer', transition: 'transform 0.2s', minHeight: '120px' }}
                            onClick={() => {
                                setSelectedWallet('main_wallet');
                                setSearchParams({ wallet: 'main_wallet' });
                            }}
                            title="Click to select Main Wallet"
                        >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="card-icon-wrapper">
                                    <FaWallet className="card-icon text-white fs-4" />
                                </div>
                                <span className={`badge ${selectedWallet === 'main_wallet' ? 'bg-white text-primary' : 'bg-light text-dark'}`} style={{ fontSize: '0.72rem' }}>
                                    {selectedWallet === 'main_wallet' ? '● Selected for Load' : 'Click to Select'}
                                </span>
                            </div>
                            <div className="text-end">
                                <h3 className="fs-3 text-white fw-bold mb-0">
                                    ₹{Number(balances.main_wallet).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </h3>
                                <p className="text-white fw-bold mb-0 mt-1" style={{ fontSize: '0.92rem', letterSpacing: '0.2px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                    Admin Virtual Main Wallet
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* AEPS Virtual Wallet Card */}
                    <div className="col-xl-4 col-md-6">
                        <div
                            className="dashboard-card card-gradient-purple p-3 shadow-sm rounded-3 cursor-pointer"
                            style={{ cursor: 'pointer', transition: 'transform 0.2s', minHeight: '120px' }}
                            onClick={() => {
                                setSelectedWallet('aeps_wallet');
                                setSearchParams({ wallet: 'aeps_wallet' });
                            }}
                            title="Click to select AEPS Wallet"
                        >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="card-icon-wrapper">
                                    <FaWallet className="card-icon text-white fs-4" />
                                </div>
                                <span className={`badge ${selectedWallet === 'aeps_wallet' ? 'bg-white text-purple' : 'bg-light text-dark'}`} style={{ fontSize: '0.72rem' }}>
                                    {selectedWallet === 'aeps_wallet' ? '● Selected for Load' : 'Click to Select'}
                                </span>
                            </div>
                            <div className="text-end">
                                <h3 className="fs-3 text-white fw-bold mb-0">
                                    ₹{Number(balances.aeps_wallet).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </h3>
                                <p className="text-white fw-bold mb-0 mt-1" style={{ fontSize: '0.92rem', letterSpacing: '0.2px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                    Admin Virtual AEPS Wallet
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Cashback Virtual Wallet Card */}
                    <div className="col-xl-4 col-md-6">
                        <div
                            className="dashboard-card card-gradient-orange p-3 shadow-sm rounded-3 cursor-pointer"
                            style={{ cursor: 'pointer', transition: 'transform 0.2s', minHeight: '120px' }}
                            onClick={() => {
                                setSelectedWallet('cashback_wallet');
                                setSearchParams({ wallet: 'cashback_wallet' });
                            }}
                            title="Click to select Cashback Wallet"
                        >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="card-icon-wrapper">
                                    <FaWallet className="card-icon text-white fs-4" />
                                </div>
                                <span className={`badge ${selectedWallet === 'cashback_wallet' ? 'bg-white text-warning' : 'bg-light text-dark'}`} style={{ fontSize: '0.72rem' }}>
                                    {selectedWallet === 'cashback_wallet' ? '● Selected for Load' : 'Click to Select'}
                                </span>
                            </div>
                            <div className="text-end">
                                <h3 className="fs-3 text-white fw-bold mb-0">
                                    ₹{Number(balances.cashback_wallet).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </h3>
                                <p className="text-white fw-bold mb-0 mt-1" style={{ fontSize: '0.92rem', letterSpacing: '0.2px', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                    Admin Virtual Cashback Wallet
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- 2. LOAD VIRTUAL MONEY FORM --- */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-white py-3 px-4 border-bottom d-flex align-items-center justify-content-between">
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">
                                <i className="fa-solid fa-circle-plus me-2 text-primary"></i>
                                Load Unlimited Virtual Money
                            </h5>
                            <small className="text-muted">
                                Target a specific wallet pool to add digital supply for retailer payment disbursements.
                            </small>
                        </div>
                        <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 rounded-pill">
                            <FaCheckCircle className="me-1" /> Option B: Isolated from Reversals
                        </span>
                    </div>

                    <div className="card-body p-4">
                        <Formik
                            enableReinitialize
                            initialValues={{
                                ...initialValues,
                                wallet_type: selectedWallet,
                            }}
                            validate={validate}
                            onSubmit={submitForm}
                        >
                            {(formik) => {
                                const {
                                    values,
                                    handleChange,
                                    handleSubmit,
                                    errors,
                                    touched,
                                    handleBlur,
                                    setFieldValue,
                                    isValid,
                                    isSubmitting,
                                } = formik;

                                return (
                                    <form onSubmit={handleSubmit}>
                                        <div className="row g-4">

                                            {/* Target Wallet Dropdown */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-secondary" style={{ fontSize: '0.9rem' }}>
                                                    Target Wallet Pool <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className="form-select form-select-lg rounded-3 shadow-none border"
                                                    name="wallet_type"
                                                    value={values.wallet_type}
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        setSelectedWallet(e.target.value);
                                                        setSearchParams({ wallet: e.target.value });
                                                    }}
                                                    onBlur={handleBlur}
                                                    style={{ height: '52px', fontSize: '0.95rem' }}
                                                >
                                                    <option value="main_wallet">Main Wallet (MB) - Standard DMT & Recharges</option>
                                                    <option value="aeps_wallet">AEPS Wallet (AW) - AEPS Transactions & Cashouts</option>
                                                    <option value="cashback_wallet">Cashback Wallet (CW) - Cashback & Incentives</option>
                                                </select>
                                                <div className="form-text text-muted">
                                                    Currently selected: <span className="fw-bold text-primary">{getWalletDisplayName(values.wallet_type)}</span>
                                                </div>
                                            </div>

                                            {/* Amount Input */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-secondary" style={{ fontSize: '0.9rem' }}>
                                                    Amount to Load (₹) <span className="text-danger">*</span>
                                                </label>
                                                <div className="input-group input-group-lg">
                                                    <span className="input-group-text bg-light text-muted fw-bold border">₹</span>
                                                    <input
                                                        type="number"
                                                        className={`form-control rounded-end-3 ${touched.amount && errors.amount ? 'is-invalid' : ''}`}
                                                        name="amount"
                                                        id="amount"
                                                        placeholder="Enter Amount (e.g. 500000)"
                                                        value={values.amount}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        onWheel={(e) => e.target.blur()}
                                                        style={{ height: '52px', fontSize: '0.95rem' }}
                                                    />
                                                </div>
                                                {touched.amount && errors.amount && (
                                                    <div className="text-danger small mt-1">{errors.amount}</div>
                                                )}
                                            </div>

                                            {/* Order ID & Operator ID */}
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-secondary" style={{ fontSize: '0.9rem' }}>
                                                    Reference / Order ID <span className="text-muted fw-normal">(Optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-lg rounded-3 border"
                                                    name="orderId"
                                                    id="orderId"
                                                    placeholder="Auto-generated if left empty"
                                                    value={values.orderId}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    style={{ height: '52px', fontSize: '0.95rem' }}
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold text-secondary" style={{ fontSize: '0.9rem' }}>
                                                    Operator / Staff ID <span className="text-muted fw-normal">(Optional)</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-lg rounded-3 border"
                                                    name="operatorId"
                                                    id="operatorId"
                                                    placeholder="Enter Operator or Staff Identifier"
                                                    value={values.operatorId}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    style={{ height: '52px', fontSize: '0.95rem' }}
                                                />
                                            </div>

                                            {/* Remarks / Message */}
                                            <div className="col-12">
                                                <label className="form-label fw-semibold text-secondary" style={{ fontSize: '0.9rem' }}>
                                                    Message / Remarks <span className="text-muted fw-normal">(Optional)</span>
                                                </label>
                                                <textarea
                                                    className="form-control rounded-3 border"
                                                    rows={2}
                                                    name="message"
                                                    id="message"
                                                    placeholder={`e.g. Loading virtual balance for ${getWalletDisplayName(values.wallet_type)}`}
                                                    value={values.message}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    style={{ fontSize: '0.95rem' }}
                                                />
                                            </div>

                                            {/* OTP Verification Section */}
                                            <div className="col-12">
                                                <div className="p-3 bg-light rounded-3 border">
                                                    <div className="row align-items-center g-3">
                                                        <div className="col-md-8">
                                                            <label className="form-label fw-semibold text-secondary mb-1" style={{ fontSize: '0.9rem' }}>
                                                                Admin Security OTP <span className="text-danger">*</span>
                                                            </label>
                                                            <div className="input-group">
                                                                <input
                                                                    type="text"
                                                                    className={`form-control form-control-lg ${touched.otp && errors.otp ? 'is-invalid' : ''}`}
                                                                    disabled={inpFlg}
                                                                    name="otp"
                                                                    id="otp"
                                                                    placeholder={inpFlg ? "Click 'Send OTP' first ➔" : "Enter the OTP received (Default: 123456)"}
                                                                    value={values.otp}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    style={{ height: '48px', fontSize: '0.95rem' }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className={`btn px-4 fw-semibold ${otpSent ? 'btn-outline-success' : 'btn-warning text-dark'}`}
                                                                    onClick={sendOtp}
                                                                    disabled={sendingOtp}
                                                                >
                                                                    {sendingOtp ? (
                                                                        <span><i className="fa-solid fa-spinner fa-spin me-1"></i> Sending...</span>
                                                                    ) : otpSent ? (
                                                                        <span><FaCheckCircle className="me-1" /> Resend OTP</span>
                                                                    ) : (
                                                                        <span><FaPaperPlane className="me-1" /> Send OTP</span>
                                                                    )}
                                                                </button>
                                                            </div>
                                                            {touched.otp && errors.otp && (
                                                                <div className="text-danger small mt-1">{errors.otp}</div>
                                                            )}
                                                        </div>

                                                        <div className="col-md-4 text-md-end text-muted small">
                                                            <p className="mb-0">
                                                                🔒 Protected by Admin Authorization. OTP expires in 60 minutes.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Error Message Display */}
                                            {errMessage && (
                                                <div className="col-12">
                                                    <div className="alert alert-danger py-2 px-3 mb-0 rounded-3">
                                                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                                        {errMessage}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="col-12 d-flex justify-content-between align-items-center pt-2">
                                                <Link
                                                    to="/admin/wallet/virtual-wallet"
                                                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold"
                                                >
                                                    <FaArrowLeft className="me-1" /> View Virtual Wallet List
                                                </Link>

                                                <button
                                                    type="submit"
                                                    className="btn btn-primary px-5 py-2 rounded-pill fw-bold shadow-sm"
                                                    disabled={!values.amount || !values.otp || isSubmitting}
                                                    style={{ minWidth: '180px', height: '46px' }}
                                                >
                                                    {isSubmitting ? (
                                                        <span><i className="fa-solid fa-spinner fa-spin me-2"></i> Loading...</span>
                                                    ) : (
                                                        <span><FaCoins className="me-2" /> Load Virtual Money</span>
                                                    )}
                                                </button>
                                            </div>

                                        </div>
                                    </form>
                                );
                            }}
                        </Formik>
                    </div>
                </div>

            </div>
            <ToastContainer />
        </>
    );
}

export default AddUpdateVirtualWall;